import React from "react";

import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { ProjectLeadActionType } from "@src/constants";
import { mapRoleRequestData } from "@src/features/resource_plan/utils/resource_plan_table_utils";
import { useAgGrid } from "@src/hooks/use_ag_grid";
import { clearResourcePlanModal } from "@src/services/slices/resourcePlanModalSlice";
import { useAddNoteMutation } from "@src/services/apis/notesApi";
import {
  useAcceptRequestV2Mutation,
  useDeclineRequestV2Mutation,
} from "@src/services/apis/projectRoleRequestsApi";
import { useAppDispatch } from "@src/setupStore";
import { Employee, Note } from "@src/types";
import {
  ResourcePlanTableRowData,
  RoleRequest,
} from "@src/types/role_request_types";
import { getInitials } from "@src/utils/helper";
import { App, Avatar, Form, Input, Modal } from "antd";
import Cookies from "universal-cookie";
import "./decline_confirmation_modal.less";

const cookies = new Cookies();

interface DeclineConfirmationModalProps {
  showModal: boolean;
  onCloseCallback: () => void;
  hasDifferentWorkload: boolean;
  actionType: ProjectLeadActionType;
  roleRequestId: number;
}

const DeclineConfirmationModal: React.FC<DeclineConfirmationModalProps> = ({
  showModal,
  onCloseCallback,
  hasDifferentWorkload,
  actionType,
  roleRequestId,
}) => {
  const { gridApi } = useAgGrid();
  const { message } = App.useApp();
  const [form] = Form.useForm<{ comment: string }>();

  const [acceptRequest] = useAcceptRequestV2Mutation();
  const [declineRequest] = useDeclineRequestV2Mutation();

  const [addNote] = useAddNoteMutation();

  const dispatch = useAppDispatch();

  const loggedInUser: Employee = cookies.get("loggedInuser");

  const isAccepted: boolean = actionType === ProjectLeadActionType.ACCEPT;

  /**
   * Sends the request to the backend
   */
  async function onSend(): Promise<void> {
    try {
      const roleRequest: RoleRequest = await sendRequest();
      message.success(`Successfully ${actionType}`, 2);

      const mappedRoleRequest: ResourcePlanTableRowData[] = mapRoleRequestData([
        roleRequest,
      ]);
      gridApi.applyTransaction({ update: mappedRoleRequest });

      await sendNote();
    } catch (error) {
      console.error(error);
    }

    dispatch(clearResourcePlanModal());
    onCloseCallback();
  }

  /**
   * Determines which endpoint should be called and calls it.
   */
  async function sendRequest(): Promise<RoleRequest> {
    if (actionType === ProjectLeadActionType.ACCEPT) {
      return await acceptRequest(roleRequestId).unwrap();
    } else if (actionType === ProjectLeadActionType.DECLINE) {
      return await declineRequest(roleRequestId).unwrap();
    }

    throw new Error("action type not known");
  }

  /**
   * Creates and send the note
   */
  async function sendNote(): Promise<void> {
    const noteText: string = form.getFieldValue("comment");

    if (!noteText) return;

    const note: Note = {
      employeeId: loggedInUser.employeeId,
      createdDate: new Date().toISOString(),
      roleRequestId: roleRequestId,
      text: noteText,
      employeeFullName: loggedInUser.fullName,
      createdBy: loggedInUser.employeeId,
    };

    await addNote(note);
    message.success("Successfully saved note", 2);
  }

  /**
   * Hides the modal when cancel is clicked
   */
  function onCancel(): void {
    onCloseCallback();
  }

  /**
   * Get the title of the Modal.
   *
   * @returns the right title
   */
  function getTitle(): string {
    if (isAccepted) {
      return `Accept DECLINED request${
        hasDifferentWorkload ? " with changed allocations" : ""
      }`;
    }

    return "Decline DECLINED request";
  }

  return (
    <Modal
      open={showModal}
      centered
      width={668}
      maskClosable={false}
      closable={false}
      title={getTitle()}
      okText="Confirm"
      okButtonProps={{
        icon: <CheckOutlined />,
        size: "large",
        "data-testid": "confirm-accept-button",
      }}
      onOk={onSend}
      cancelButtonProps={{
        icon: <CloseOutlined />,
        size: "large",
      }}
      onCancel={onCancel}
      cancelText="Close"
      className="decline-confirmation-modal"
    >
      Are you sure you want to {actionType} the request?
      <ul>
        <li>Request will go back to state "REQUESTED".</li>

        {hasDifferentWorkload &&
          (isAccepted ? (
            <li>
              Allocations will be updated to the suggested workload by the team
              lead.
            </li>
          ) : (
            <li>
              The suggested allocations by the team lead will NOT be updated.
            </li>
          ))}

        {!isAccepted && <li>Team lead will see "DECLINED" Tag on request.</li>}
      </ul>
      {!isAccepted && (
        <>
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Add Comment"
              name="comment"
              className="comment-box"
            >
              <div className="avatar-comment-section">
                <Avatar>{getInitials(loggedInUser.fullName)}</Avatar>
                <Input.TextArea
                  autoSize={false}
                  rows={4}
                  style={{ resize: "none" }}
                />
              </div>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default DeclineConfirmationModal;
